"use client"

import { useState, useEffect } from "react"
import {
  Table,
  Spin,
  Modal,
  Button,
  Card,
  Space,
  Typography,
  Divider,
  Tag,
  Row,
  Col,
  Input,
  Breadcrumb,
  Empty,
  Tooltip,
  Badge,
} from "antd"
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  HomeOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons"
import { toast } from "react-toastify"
import { api } from "../../services/apiClient"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import styles from "./style.module.scss"

const { Title, Text } = Typography
const { Search } = Input

// Lista de perguntas do checklist
const questionsList = [
  "Na data da integralização do capital social, os valores integralizados possuíam origem lícita e comprovada para a constituição da empresa, ou seja, existia lastro dos valores integralizados?",
  "A estrutura atual da empresa é compatível com o volume de operação e atividades desenvolvidas?",
  "A empresa vem registrando regularmente seu faturamento ou receita bruta, além de vendas de mercadorias ou prestação de serviços de forma compatível com a emissão de notas de entrada e saída de valores em sua conta corrente e contabilidade?",
  "Há compatibilidade em relação aos valores movimentados em conta corrente bancária e valores lançados na contabilidade?",
  "Há procuração para terceiros operarem as contas correntes da empresa?",
  "A empresa já passou por algum procedimento especial de fiscalização aduaneira ou canal cinza? Sofreu alguma penalidade aduaneira como por exemplo multa aduaneira ou perdimento?",
  "Considerando que é necessário provar a origem lícita dos recursos empregados nas importações, há registros e documentos comprobatórios da origem lícita dos recursos utilizados nas operação de importação?",
  "Existem responsáveis pela negociação de valores das mercadorias importadas e registro dos meios pelos quais foram feitas as negociações (e-mail, WhatsApp, WeChat, telefone, entre outros) que comprovem a efetiva negociação, qualidade do produto, quantidade do produto bem como tabela de preços e valor das mercadorias importadas?",
  "Houve contrato de câmbio e sua respectiva liquidação? Em caso negativo, há prova de prazo para pagamento?",
  "Houve contrato de seguro da mercadoria?",
  "Sobre a contratação de intermediação na importação foi contratada a prestação de serviços de despachante aduaneiro para a intermediação e conclusão da importação?",
  "É feita uma checagem prévia acerca do eventual registro de marca referente à mercadoria importada?",
  "São tomados cuidados mínimos para auferir a regularidade do comprador no mercado interno das mercadorias importadas, tais como se o comprador existe de fato e de direito e se existem registros da negociação e venda das mercadorias bem como recebimento dos valores pelas mercadorias negociadas?",
  "Existe margem de lucro razoável e compatível com o mercado das mercadorias importadas e vendidas no mercado interno?",
  "As importações realizadas estão em conformidade com as áreas de atuação registradas no contrato social e Cnae da empresa?",
]

export default function CheckListDashboard() {
  const [loading, setLoading] = useState(true)
  const [checklists, setChecklists] = useState([])
  const [filteredChecklists, setFilteredChecklists] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedChecklist, setSelectedChecklist] = useState(null)
  const [searchText, setSearchText] = useState("")

  // Busca todos os checklists do back-end
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await api.get("/checklist")
        setChecklists(response.data)
        setFilteredChecklists(response.data)
      } catch (error) {
        toast.error("Erro ao buscar dados do checklist.")
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Filtro de busca
  useEffect(() => {
    if (!searchText) {
      setFilteredChecklists(checklists)
      return
    }

    const filtered = checklists.filter(
      (checklist) =>
        checklist.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        checklist.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        checklist.phone?.includes(searchText) ||
        checklist.id?.toString().includes(searchText),
    )
    setFilteredChecklists(filtered)
  }, [checklists, searchText])

  // Abre o modal e seta o checklist selecionado
  const handleViewDetails = (record) => {
    setSelectedChecklist(record)
    setModalOpen(true)
  }

  // Fecha o modal
  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedChecklist(null)
  }

  // Gera o PDF
  const handleGeneratePDF = () => {
    const modalContent = document.getElementById("checklist-modal-content")
    if (!modalContent) return

    html2canvas(modalContent, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png")
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = pdf.internal.pageSize.getWidth()
      const imgProps = pdf.getImageProperties(imgData)
      const pdfHeight = (imgProps.height * pageWidth) / imgProps.width

      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pdfHeight)
      pdf.save(`checklist-${selectedChecklist?.name || selectedChecklist?.id}.pdf`)
    })
  }

  // Função para contar respostas
  const getAnswerStats = (answers) => {
    if (!answers || !Array.isArray(answers)) return { sim: 0, nao: 0, total: 0 }

    const sim = answers.filter((answer) => answer?.toLowerCase() === "sim").length
    const nao = answers.filter((answer) => answer?.toLowerCase() === "não" || answer?.toLowerCase() === "nao").length
    const total = answers.length

    return { sim, nao, total }
  }

  // Função para obter cor do status baseado nas respostas
  const getStatusColor = (answers) => {
    const stats = getAnswerStats(answers)
    if (stats.total === 0) return "default"

    const percentage = (stats.sim / stats.total) * 100
    if (percentage >= 80) return "success"
    if (percentage >= 60) return "warning"
    return "error"
  }

  // Função para obter ícone da resposta
  const getAnswerIcon = (answer) => {
    const lowerAnswer = answer?.toLowerCase()
    if (lowerAnswer === "sim") return <CheckCircleOutlined style={{ color: "#52c41a" }} />
    if (lowerAnswer === "não" || lowerAnswer === "nao") return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
    return <QuestionCircleOutlined style={{ color: "#faad14" }} />
  }

  // Definindo as colunas da tabela
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Badge count={id} style={{ backgroundColor: "#667eea" }} />,
    },
    {
      title: "Informações do Cliente",
      key: "clientInfo",
      render: (_, record) => (
        <div className={styles.clientInfo}>
          <div className={styles.clientName}>
            <UserOutlined /> {record.name}
          </div>
          <div className={styles.clientDetails}>
            <div className={styles.clientDetail}>
              <MailOutlined /> {record.email}
            </div>
            <div className={styles.clientDetail}>
              <PhoneOutlined /> {record.phone}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Status do Checklist",
      key: "status",
      width: 200,
      render: (_, record) => {
        const stats = getAnswerStats(record.answers)
        const statusColor = getStatusColor(record.answers)

        return (
          <div className={styles.statusContainer}>
            <Tag color={statusColor} className={styles.statusTag}>
              {stats.total > 0 ? `${stats.sim}/${stats.total} Conformes` : "Sem respostas"}
            </Tag>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: stats.total > 0 ? `${(stats.sim / stats.total) * 100}%` : "0%",
                  backgroundColor:
                    statusColor === "success" ? "#52c41a" : statusColor === "warning" ? "#faad14" : "#ff4d4f",
                }}
              />
            </div>
          </div>
        )
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver Detalhes">
            <Button type="primary" icon={<EyeOutlined />} size="small" onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title="Baixar PDF">
            <Button type="default" icon={<DownloadOutlined />} size="small" onClick={() => handleViewDetails(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <Breadcrumb
            items={[
              {
                href: "",
                title: <HomeOutlined />,
              },
              {
                href: "",
                title: <span>Compliance</span>,
              },
              {
                title: "CheckList Dashboard",
              },
            ]}
            className={styles.breadcrumb}
          />
        </div>

        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <Title level={2} className={styles.pageTitle}>
              <FileTextOutlined /> CheckList Dashboard
            </Title>
            <Text className={styles.pageDescription}>
              Visualize e gerencie todos os checklists de compliance dos clientes
            </Text>
          </div>

          <div className={styles.statsCards}>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{filteredChecklists.length}</div>
                <div className={styles.statLabel}>Total</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>
                  {filteredChecklists.filter((c) => getStatusColor(c.answers) === "success").length}
                </div>
                <div className={styles.statLabel}>Conformes</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>
                  {filteredChecklists.filter((c) => getStatusColor(c.answers) === "warning").length}
                </div>
                <div className={styles.statLabel}>Atenção</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>
                  {filteredChecklists.filter((c) => getStatusColor(c.answers) === "error").length}
                </div>
                <div className={styles.statLabel}>Críticos</div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={16} md={18}>
            <Search
              placeholder="Buscar por nome, email, telefone ou ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              className={styles.searchInput}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} block>
              Atualizar
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card className={styles.tableCard}>
        <Spin spinning={loading}>
          <Table
            dataSource={filteredChecklists}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            }}
            className={styles.checklistTable}
            locale={{
              emptyText: <Empty description="Nenhum checklist encontrado" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
          />
        </Spin>
      </Card>

      {/* Modal de Detalhes */}
      <Modal
        open={modalOpen}
        title={
          <div className={styles.modalHeader}>
            <FileTextOutlined />
            <span>Detalhes do Checklist</span>
          </div>
        }
        onCancel={handleCloseModal}
        width={1000}
        footer={[
          <Button key="fechar" onClick={handleCloseModal}>
            Fechar
          </Button>,
          <Button key="pdf" type="primary" icon={<DownloadOutlined />} onClick={handleGeneratePDF}>
            Gerar PDF
          </Button>,
        ]}
        className={styles.detailModal}
      >
        {selectedChecklist && (
          <div id="checklist-modal-content" className={styles.modalContent}>
            {/* Informações do Cliente */}
            <div className={styles.clientHeader}>
              <div className={styles.clientAvatar}>
                <UserOutlined />
              </div>
              <div className={styles.clientMainInfo}>
                <h3>{selectedChecklist.name}</h3>
                <div className={styles.clientContactInfo}>
                  <span>
                    <MailOutlined /> {selectedChecklist.email}
                  </span>
                  <span>
                    <PhoneOutlined /> {selectedChecklist.phone}
                  </span>
                </div>
              </div>
              <div className={styles.clientStats}>
                {(() => {
                  const stats = getAnswerStats(selectedChecklist.answers)
                  return (
                    <div className={styles.statsGrid}>
                      <div className={styles.statItem}>
                        <div className={styles.statValue}>{stats.total}</div>
                        <div className={styles.statLabel}>Total</div>
                      </div>
                      <div className={styles.statItem}>
                        <div className={styles.statValue} style={{ color: "#52c41a" }}>
                          {stats.sim}
                        </div>
                        <div className={styles.statLabel}>Sim</div>
                      </div>
                      <div className={styles.statItem}>
                        <div className={styles.statValue} style={{ color: "#ff4d4f" }}>
                          {stats.nao}
                        </div>
                        <div className={styles.statLabel}>Não</div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>

            <Divider />

            {/* Respostas do Checklist */}
            <div className={styles.answersSection}>
              <Title level={4} className={styles.sectionTitle}>
                <CheckCircleOutlined /> Respostas do Checklist
              </Title>

              {selectedChecklist.answers && selectedChecklist.answers.length > 0 ? (
                <div className={styles.questionsList}>
                  {selectedChecklist.answers.map((answer, index) => (
                    <Card key={index} className={styles.questionCard} size="small">
                      <div className={styles.questionHeader}>
                        <div className={styles.questionNumber}>#{index + 1}</div>
                        <div className={styles.answerIcon}>{getAnswerIcon(answer)}</div>
                      </div>
                      <div className={styles.questionContent}>
                        <div className={styles.questionText}>{questionsList[index] ?? `Pergunta ${index + 1}`}</div>
                        <div className={styles.answerText}>
                          <strong>Resposta:</strong>{" "}
                          <Tag
                            color={
                              answer?.toLowerCase() === "sim"
                                ? "success"
                                : answer?.toLowerCase() === "não" || answer?.toLowerCase() === "nao"
                                  ? "error"
                                  : "warning"
                            }
                          >
                            {answer}
                          </Tag>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty description="Nenhuma resposta registrada" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
